import Utils from './utils';

export default class URLParamsHelper<T extends Record<string, unknown>> {
    public readonly url: URL;

    private readonly params: T;

    private path: string;

    constructor(params: T, path: string) {
        this.params = params;
        this.path = path;

        if (path.startsWith('http://') || path.startsWith('https://')) {
            this.url = new URL(path);
        }
        else {
            this.url = new URL(
                path,
                import.meta.env.VITE_DOMEN_URL
            );
        }
    }

    paramKey(): string[] {
        return Utils.isObject(this.params)
            ? Object.keys(this.params)
            : [];
    }

    getParams(): T {
        const allParams = this.getAllParams();
        return this.paramKey().reduce(
            (acc, param) => {
                if (allParams[param] !== undefined) {
                    if (typeof this.params[param] === 'number') {
                        acc[param] = Number(allParams[param]);
                    }
                    else {
                        acc[param] = allParams[param];
                    }
                }
                else {
                    acc[param] = this.params[param];
                }
                return acc;
            },
            {} as Record<string, unknown>
        ) as T;
    }

    getUrl(): URL {
        return this.url;
    }

    buildParams(): string {
        Object.entries(this.params).forEach(([
            key,
            value
        ]) => {
            this.setUrlSearchParams(
                key,
                value
            );
        });
        return this.url.search;
    }

    buildUrlWithParams(): string {
        return this.getUrl() + this.buildParams();
    }

    getAllParams(): Record<string, unknown> | Partial<T> {
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        const paramsObject: Record<string, any> = {};
        for (const [
            key,
            value
        ] of Array.from(this.url.searchParams.entries())) {
            const keys = key.match(/([^[\]]+)(?=\[|]|$)/g) || [];
            keys.reduce(
                (acc, keyPart, index) => {
                    if (index === keys.length - 1) {
                        acc[keyPart] = decodeURIComponent(value);
                        return acc;
                    }
                    if (!acc[keyPart]) {
                        acc[keyPart] = (/\d+/).test(keys[index + 1] ?? '')
                            ? []
                            : {};
                    }
                    return acc[keyPart];
                },
                paramsObject
            );
        }

        return paramsObject;
    }

    private setUrlSearchParams(key: string, value: unknown) {
        if (value === null || value === undefined) return;
        if (Utils.isObject(value)) {
            Object.entries(value).forEach(([
                subKey,
                subValue
            ]) => {
                if (subValue !== null && subValue !== undefined) {
                    this.setUrlSearchParams(
                        `${key}[${subKey}]`,
                        subValue
                    );
                }
            });
        }
        else if (Utils.isArray(value)) {
            value.forEach((item, index) => {
                if (item !== null && item !== undefined) {
                    this.setUrlSearchParams(
                        `${key}[${index}]`,
                        item
                    );
                }
            });
        }
        else {
            this.url.searchParams.set(
                key,
                String(value)
            );
        }
    }
}
